/* eslint-disable @typescript-eslint/naming-convention */
import { cssVar, theme as defaultTheme, mergeThemeOverride, } from "@chakra-ui/react";
import pick from "lodash/pick";
import omit from "lodash/omit";
import colors from "./colors";
export const theme = {
    ...defaultTheme,
    components: mergeThemeOverride({}, omit(defaultTheme.components, "Radio", "Select"), {
        Select: {
            parts: ["toggleButton", "value", "icon"],
            baseStyle: {
                toggleButton: {
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "base.lightBackground",
                    borderWidth: "1px",
                    borderColor: "base.border",
                    borderRadius: "md",
                    boxShadow: "xs",
                    transition: "200ms all",
                    maxWidth: "100%",
                    outline: "none",
                    color: "text.tertiary",
                    _selected: {
                        color: "text.primary",
                    },
                    _hover: {
                        borderColor: "gray.border",
                    },
                    _expanded: {
                        backgroundColor: "white",
                        borderColor: "primary.base",
                        _hover: {
                            borderColor: "primary.base",
                        },
                    },
                    _disabled: {
                        backgroundColor: "gray.200",
                        cursor: "not-allowed",
                        _selected: {
                            color: "text.tertiary",
                        },
                        _hover: {
                            borderColor: "base.border",
                        },
                    },
                    _loading: {
                        cursor: "wait",
                    },
                    _invalid: {
                        borderColor: "danger.base",
                        _hover: {
                            borderColor: "danger.base",
                        },
                    },
                    // Override app styles
                    _focus: {
                        outline: "none !important",
                    },
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                },
                value: {
                    flex: "1",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                    mr: 2,
                },
            },
            sizes: {
                md: {
                    toggleButton: {
                        height: 8,
                        px: 2,
                    },
                    value: {
                        fontSize: "md",
                    },
                    icon: {
                        boxSize: 4,
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        Combobox: {
            parts: ["control", "input", "toggleButton", "toggleButtonIcon"],
            baseStyle: {
                control: {
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "base.lightBackground",
                    borderWidth: "1px",
                    borderColor: "base.border",
                    borderRadius: "md",
                    boxShadow: "xs",
                    transition: "200ms all",
                    maxWidth: "100%",
                    color: "text.primary",
                    _hover: {
                        borderColor: "gray.border",
                    },
                    _expanded: {
                        backgroundColor: "white",
                        borderColor: "primary.base",
                        _hover: {
                            borderColor: "primary.base",
                        },
                    },
                    _disabled: {
                        backgroundColor: "gray.200",
                        cursor: "not-allowed",
                        _selected: {
                            color: "text.tertiary",
                        },
                        _hover: {
                            borderColor: "base.border",
                        },
                    },
                    _loading: {
                        cursor: "wait",
                    },
                    _invalid: {
                        borderColor: "danger.base",
                        _hover: {
                            borderColor: "danger.base",
                        },
                    },
                    // Override app styles
                    _focus: {
                        outline: "none !important",
                    },
                    "&[data-focus-visible]": {
                        boxShadow: "outline",
                    },
                },
                input: {
                    flex: "1",
                    minWidth: 0,
                    height: "100%",
                    bg: "transparent",
                    fontSize: "md",
                    outline: "none",
                    pr: 2,
                    color: "inherit",
                    cursor: "inherit",
                    _placeholder: {
                        color: "text.tertiary",
                    },
                },
                toggleButton: {
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    // Override app styles
                    _focus: {
                        outline: "none !important",
                    },
                    _disabled: {
                        cursor: "not-allowed",
                    },
                },
            },
            sizes: {
                md: {
                    control: {
                        height: 8,
                        px: 2,
                    },
                    icon: {
                        boxSize: 4,
                    },
                    toggleButtonIcon: {
                        boxSize: 4,
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        TagInput: {
            parts: [
                "tagInput",
                "tags",
                "tag",
                "tagAccessory",
                "tagLabel",
                "tagRemoveButton",
                "tagRemoveButtonIcon",
                "input",
                "inputContainer",
                "toggleButton",
                "toggleButtonIcon",
            ],
            baseStyle: {
                tagInput: {
                    display: "flex",
                    gap: 2,
                    backgroundColor: "base.lightBackground",
                    borderWidth: "1px",
                    borderColor: "base.border",
                    borderRadius: "md",
                    boxShadow: "xs",
                    transition: "200ms all",
                    maxWidth: "100%",
                    color: "text.primary",
                    _hover: {
                        borderColor: "gray.border",
                    },
                    _expanded: {
                        backgroundColor: "white",
                        borderColor: "primary.base",
                        _hover: {
                            borderColor: "primary.base",
                        },
                    },
                    _disabled: {
                        backgroundColor: "gray.200",
                        cursor: "not-allowed",
                        _selected: {
                            color: "text.tertiary",
                        },
                        _hover: {
                            borderColor: "base.border",
                        },
                    },
                    _loading: {
                        cursor: "wait",
                    },
                    _invalid: {
                        borderColor: "danger.base",
                        _hover: {
                            borderColor: "danger.base",
                        },
                    },
                    // Override app styles
                    _focus: {
                        outline: "none !important",
                    },
                    "&[data-focus-visible]": {
                        boxShadow: "outline",
                    },
                },
                tags: {
                    flex: "1",
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    px: 2,
                    py: 1,
                },
                tag: {
                    maxWidth: "100%",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    outline: "none",
                    bg: "gray.200",
                    borderRadius: "full",
                    pl: 2,
                    pr: 1.5,
                    "&[data-has-image-accessory]": {
                        pl: 0,
                    },
                    _disabled: {
                        bg: "gray.300",
                        pr: 2,
                    },
                    _focus: {
                        bg: "gray.300",
                    },
                },
                tagAccessory: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    bg: "white",
                    borderRadius: "full",
                    borderWidth: "2px",
                    borderColor: "base.border",
                    '&[data-status="success"]': {
                        borderColor: "grass.500",
                    },
                    '&[data-status="error"]': {
                        borderColor: "danger.500",
                    },
                    '&[data-status="processing"]': {
                        borderColor: "teal.500",
                    },
                    '&[data-status="warning"]': {
                        borderColor: "warning.500",
                    },
                    '&[data-status="inactive"]': {
                        borderColor: "gray.400",
                    },
                },
                tagAccessoryImage: {
                    flex: "none",
                    objectFit: "contain",
                    width: "100%",
                    height: "100%",
                },
                tagLabel: {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "md",
                },
                tagRemoveButton: {
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    outline: "none",
                    minWidth: 0,
                    height: "auto",
                    cursor: "pointer",
                },
                inputContainer: {
                    flex: "1",
                    minWidth: "96px",
                    display: "flex",
                    ml: -1,
                },
                input: {
                    width: "100%",
                    height: "100%",
                    bg: "transparent",
                    fontSize: "md",
                    outline: "none",
                    px: 2,
                    color: "inherit",
                    cursor: "inherit",
                    _placeholder: {
                        color: "text.tertiary",
                    },
                },
                toggleButton: {
                    display: "flex",
                    alignItems: "center",
                    pr: 2,
                    // Override app styles
                    _focus: {
                        outline: "none !important",
                    },
                    _disabled: {
                        cursor: "not-allowed",
                    },
                },
            },
            sizes: {
                md: {
                    tagInput: {
                        minHeight: 8,
                    },
                    tag: {
                        h: 5,
                    },
                    tagAccessory: {
                        boxSize: 5,
                    },
                    tagRemoveButtonIcon: {
                        boxSize: 3,
                    },
                    inputContainer: {
                        h: 5,
                    },
                    icon: {
                        boxSize: 4,
                    },
                    toggleButtonIcon: {
                        boxSize: 4,
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        SelectOptions: {
            parts: ["container", "list", "item"],
            baseStyle: {
                container: {
                    height: "100%",
                    overflowY: "auto",
                },
                list: {
                    position: "relative",
                },
                item: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                },
            },
        },
        SelectOption: {
            parts: ["option", "label", "checkIcon"],
            baseStyle: {
                option: {
                    backgroundColor: "white",
                    color: "text.primary",
                    px: 2,
                    display: "flex",
                    outline: "none",
                    _selected: {
                        bg: "gray.background",
                    },
                    _highlighted: {
                        bg: "forest.200",
                    },
                    _disabled: {
                        color: "text.tertiary",
                    },
                },
                label: {
                    flex: "1",
                    minWidth: 0,
                    fontSize: "md",
                    lineHeight: "5",
                    userSelect: "none",
                    py: 1.5,
                    mr: 3,
                },
                checkIcon: {
                    color: "primary.base",
                    alignSelf: "center",
                    opacity: 0,
                    _selected: {
                        opacity: 1,
                    },
                },
            },
            sizes: {
                md: {
                    checkIcon: {
                        boxSize: 4,
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        SelectOptionAccessory: {
            parts: ["container", "accessory"],
            baseStyle: {
                container: {
                    flex: "none",
                    display: "flex",
                    alignItems: "center",
                    mr: 2,
                },
                accessory: {
                    flex: "none",
                },
            },
            sizes: {
                md: {
                    container: {
                        height: 8,
                    },
                    accessory: {
                        boxSize: 4,
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        SelectGroup: {
            parts: ["container", "label"],
            baseStyle: {
                container: {
                    display: "flex",
                    alignItems: "flex-end",
                    px: 2,
                    py: 1,
                },
                label: {
                    fontWeight: "medium",
                    textTransform: "uppercase",
                    letterSpacing: "wide",
                    color: "text.tertiary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                },
            },
            sizes: {
                md: {
                    container: {
                        h: 8,
                    },
                    label: {
                        fontSize: "sm",
                        lineHeight: "5",
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        SelectEmptyState: {
            baseStyle: {
                fontSize: "md",
                lineHeight: "5",
                color: "text.secondary",
                textAlign: "center",
                p: 3,
            },
        },
        ClearSelectButton: {
            parts: ["button", "icon"],
            baseStyle: {
                button: {
                    display: "flex",
                    outline: "none",
                    borderRadius: "md",
                    color: "text.primary",
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                    _disabled: {
                        color: "text.tertiary",
                        cursor: "not-allowed",
                    },
                },
                icon: {
                    boxSize: "100%",
                    flex: "none",
                },
            },
            sizes: {
                md: {
                    button: {
                        boxSize: 4,
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        Button: {
            sizes: {
                sm: {
                    h: 6,
                    fontSize: "sm",
                    lineHeight: "4",
                    px: 2,
                },
                md: {
                    h: 8,
                    fontSize: "md",
                    lineHeight: "5",
                    px: 4,
                },
                lg: {
                    h: 10,
                    fontSize: "md",
                    lineHeight: "5",
                    px: 4,
                },
                xl: {
                    h: 12,
                    fontSize: "lg",
                    lineHeight: "6",
                    px: 4,
                },
            },
            baseStyle: {
                borderRadius: "md",
                transitionDuration: "faster",
                _focus: {
                    // Override app styles
                    outline: "none !important",
                },
            },
            variants: {
                solid({ colorScheme }) {
                    const background = `${colorScheme}.base`;
                    const hoverBackground = `${colorScheme}.hover`;
                    const disabledBackground = `${colorScheme}.300`;
                    const activeBackground = `${colorScheme}.dark`;
                    return {
                        bg: background,
                        _hover: {
                            bg: hoverBackground,
                            _disabled: {
                                bg: disabledBackground,
                            },
                        },
                        _disabled: {
                            opacity: 1,
                            bg: disabledBackground,
                        },
                        _active: {
                            bg: activeBackground,
                        },
                        _focusVisible: {
                            boxShadow: colorScheme === "danger" ? "outlineRed" : "outline",
                        },
                    };
                },
                upsell: {
                    bg: "upsell.base",
                    color: "white",
                    _hover: {
                        bg: "upsell.hover",
                        _disabled: {
                            bg: "space.300",
                        },
                    },
                    _disabled: {
                        opacity: 1,
                        bg: "space.300",
                    },
                    _active: {
                        bg: "upsell.dark",
                    },
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                },
                outline: ({ colorScheme }) => ({
                    color: colorScheme === "danger" ? "danger.600" : "text.primary",
                    borderColor: colorScheme === "danger" ? "danger.600" : "gray.500",
                    bg: "white",
                    _hover: {
                        color: colorScheme === "danger" ? "danger.hover" : "text.primary",
                    },
                    _focusVisible: {
                        boxShadow: colorScheme === "danger" ? "outlineRed" : "outline",
                    },
                }),
                ghost: ({ colorScheme }) => ({
                    _focusVisible: {
                        boxShadow: colorScheme === "danger" ? "outlineRed" : "outline",
                    },
                }),
            },
        },
        Code: {
            baseStyle: {
                display: "inline",
                paddingY: 0.5,
            },
            variants: {
                subtle: {
                    bg: "gray.background",
                },
            },
        },
        Heading: {
            sizes: {
                "heading-md": {
                    fontFamily: "heading",
                    fontWeight: "medium",
                    fontSize: "xl",
                    lineHeight: "6",
                },
                "heading-lg": {
                    fontFamily: "sharpSansDisplayNo1",
                    fontWeight: "semibold",
                    fontSize: "2xl",
                    lineHeight: "6",
                },
                "heading-xl": {
                    fontFamily: "sharpSansDisplayNo1",
                    fontWeight: "semibold",
                    fontSize: "3xl",
                    lineHeight: "8",
                },
                "heading-2xl": {
                    fontFamily: "sharpSansDisplayNo1",
                    fontWeight: "semibold",
                    fontSize: "4xl",
                    lineHeight: "12",
                },
                "upsell-md": {
                    fontSize: "xl",
                    lineHeight: "6",
                },
                "upsell-lg": {
                    fontSize: "2xl",
                    lineHeight: "6",
                },
                "upsell-xl": {
                    fontSize: "3xl",
                    lineHeight: "8",
                },
                "upsell-2xl": {
                    fontSize: "4xl",
                    lineHeight: "12",
                },
            },
            variants: {
                heading: {
                    color: "text.primary",
                },
                section: {
                    fontFamily: "heading",
                    fontSize: "lg",
                    lineHeight: "6",
                    fontWeight: "medium",
                    color: "text.primary",
                },
                upsell: {
                    fontFamily: "sharpSansDisplayNo1",
                    fontWeight: "semibold",
                    color: "upsell.dark",
                },
            },
        },
        Text: {
            sizes: {
                lg: {
                    fontSize: "lg",
                    lineHeight: "6",
                },
                md: {
                    fontSize: "md",
                    lineHeight: "5",
                },
                sm: {
                    fontSize: "sm",
                    lineHeight: "4",
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        Link: {
            baseStyle: {
                color: "link.default",
                borderRadius: "sm",
                fontWeight: "medium",
                _hover: {
                    color: "link.hover",
                    textDecoration: "none",
                },
                _active: {
                    color: "link.pressed",
                },
            },
            sizes: {
                md: {
                    fontSize: "md",
                    lineHeight: "5",
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        Avatar: {
            parts: ["container"],
            baseStyle: {
                container: {
                    bg: "gray.background",
                },
                label: {
                    color: "text.secondary",
                    userSelect: "none",
                },
            },
            sizes: {
                md: {
                    container: {
                        width: 8,
                        height: 8,
                        fontSize: "sm",
                        lineHeight: "4",
                    },
                },
            },
        },
        Switch: {
            sizes: {
                sm: {
                    track: {
                        "--switch-track-width": "24px",
                        "--switch-track-height": "16px",
                        "--switch-thumb-x": "8px",
                    },
                    thumb: {
                        boxSize: 3,
                    },
                },
                md: {
                    track: {
                        "--switch-track-width": "40px",
                        "--switch-track-height": "24px",
                        "--switch-thumb-x": "16px",
                    },
                    thumb: {
                        boxSize: 5,
                    },
                },
            },
            baseStyle: {
                track: {
                    bg: "gray.300",
                    boxSizing: "border-box",
                    _disabled: {
                        opacity: 1,
                        bg: "base.background",
                    },
                    _hover: {
                        bg: "gray.400",
                        _disabled: {
                            bg: "base.background",
                        },
                    },
                    _checked: {
                        bg: "primary.base",
                        _disabled: {
                            bg: "forest.300",
                        },
                        _hover: {
                            bg: "primary.hover",
                            _disabled: {
                                bg: "forest.300",
                            },
                        },
                    },
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                },
                thumb: {
                    transition: "all 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                    boxShadow: "sm",
                },
            },
            defaultProps: {
                colorScheme: null,
            },
        },
        Checkbox: {
            parts: ["control"],
            sizes: {
                md: {
                    control: {
                        width: 4,
                        height: 4,
                    },
                    icon: {
                        fontSize: "9px",
                    },
                },
            },
            baseStyle: {
                control: {
                    borderRadius: "5px",
                    borderWidth: "1px",
                    borderColor: "gray.border",
                    transition: "border-color 100ms cubic-bezier(0.4, 0, 1, 1), background-color 100ms cubic-bezier(0.4, 0, 1, 1), box-shadow 200ms cubic-bezier(0.4, 0, 1, 1)",
                    bg: "white",
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                    _hover: {
                        borderColor: "primary.base",
                        bg: "primary.background",
                    },
                    _checked: {
                        bg: "primary.background",
                        borderColor: "primary.base",
                        color: "primary.base",
                        _disabled: {
                            bg: "gray.background",
                            color: "gray.400",
                        },
                        _hover: {
                            bg: "primary.background",
                        },
                    },
                    _indeterminate: {
                        bg: "primary.background",
                        borderColor: "primary.base",
                        color: "primary.base",
                    },
                    _disabled: {
                        bg: "gray.background",
                        borderColor: "gray.200",
                        color: "gray.400",
                    },
                    _invalid: {
                        borderColor: "danger.base",
                    },
                },
                label: {
                    fontWeight: "medium",
                    color: "text.primary",
                    ml: 2,
                },
            },
            defaultProps: {
                colorScheme: "forest",
            },
        },
        Tooltip: {
            baseStyle: {
                "--tooltip-bg": "var(--chakra-colors-gray-900)",
                px: 3,
                py: 2,
                borderRadius: "lg",
                fontSize: "sm",
                fontWeight: "medium",
                lineHeight: "4",
                textAlign: "center",
            },
        },
        Kbd: {
            baseStyle: {
                "--kbd-bg": "var(--chakra-colors-gray-800)",
                border: "none",
                borderRadius: "sm",
                color: "gray.300",
                fontFamily: "inherit",
                fontSize: "sm",
                fontWeight: "normal",
                lineHeight: "4",
                minWidth: 5,
                height: 5,
                textAlign: "center",
                px: 1,
                py: 0.5,
            },
        },
        Badge: {
            baseStyle: {
                fontSize: "sm",
                // Subtract 1px twice from 24px to account for top and bottom borders
                lineHeight: "22px",
                height: "6",
                borderWidth: "1px",
                borderStyle: "solid",
                maxW: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            variants: {
                subtle: {
                    px: 2,
                    bg: "white",
                    color: "text.secondary",
                    borderColor: "base.border",
                    borderRadius: "md",
                    fontWeight: "semibold",
                    letterSpacing: "0.03em",
                },
                upsell: {
                    px: 2.5,
                    bg: "linear-gradient(to bottom right, #ECFBFF, #e1efff, #D5E3FF)",
                    color: "upsell.base",
                    borderColor: "upsell.border",
                    borderRadius: "full",
                    fontWeight: "semibold",
                    letterSpacing: "0.03em",
                },
                label: {
                    px: 2,
                    bg: "gray.200",
                    color: "text.primary",
                    borderColor: "gray.200",
                    borderRadius: "full",
                    fontWeight: "normal",
                    textTransform: "unset",
                },
                status: {
                    width: "104px",
                    borderRadius: "md",
                    textAlign: "center",
                    letterSpacing: "0.03em",
                    fontWeight: "semibold",
                },
                destination: {
                    px: 2,
                    bg: "white",
                    color: "ocean.600",
                    borderColor: "gray.300",
                    borderRadius: "md",
                    fontWeight: "normal",
                    textTransform: "unset",
                },
            },
        },
        Input: {
            baseStyle: {
                field: {
                    transitionDuration: "faster",
                },
            },
            variants: {
                outline: {
                    field: {
                        borderColor: "base.border",
                        bg: "base.lightBackground",
                        boxShadow: "xs",
                        color: "text.primary",
                        _hover: {
                            borderColor: "gray.400",
                            _disabled: {
                                borderColor: "base.border",
                            },
                            _invalid: {
                                borderColor: "danger.600",
                            },
                        },
                        _disabled: {
                            opacity: 1,
                            bg: "gray.200",
                            color: "text.tertiary",
                        },
                        _invalid: {
                            borderColor: "danger.600",
                            boxShadow: "xs",
                        },
                        _focus: {
                            bg: "white",
                            borderColor: "primary.base",
                            boxShadow: "unset",
                            _invalid: {
                                borderColor: "red.600",
                            },
                        },
                        _focusVisible: {
                            boxShadow: "outline",
                        },
                        _placeholder: {
                            color: "text.tertiary",
                        },
                    },
                },
            },
            sizes: {
                md: {
                    field: {
                        height: 8,
                        px: 2,
                        borderRadius: "md",
                    },
                },
            },
        },
        Textarea: {
            variants: {
                outline: {
                    borderColor: "base.border",
                    borderRadius: "md",
                    bg: "base.lightBackground",
                    boxShadow: "xs",
                    color: "text.primary",
                    px: 2,
                    py: 1.5,
                    _hover: {
                        borderColor: "gray.400",
                        _disabled: {
                            borderColor: "base.border",
                        },
                        _invalid: {
                            borderColor: "danger.600",
                        },
                    },
                    _disabled: {
                        opacity: 1,
                        bg: "gray.200",
                        color: "text.tertiary",
                    },
                    _invalid: {
                        borderColor: "danger.600",
                        boxShadow: "xs",
                    },
                    _focus: {
                        bg: "white",
                        borderColor: "primary.base",
                        boxShadow: "unset",
                        _invalid: {
                            borderColor: "red.600",
                        },
                    },
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                    _placeholder: {
                        color: "text.tertiary",
                    },
                },
                addon: {
                    bg: "base.lightBackground",
                    color: "text.tertiary",
                },
            },
        },
        Form: {
            baseStyle: {
                helperText: {
                    mt: 1,
                    fontSize: "md",
                    lineHeight: "5",
                    maxWidth: "xl",
                    // --form-control-color is defined by Chakra and is used in `FormHelperText` text
                    [cssVar("form-control-color").variable]: "colors.text.secondary",
                    // --hightouch-text-color makes nested `Text` components inherit this color
                    [cssVar("hightouch-text-color").variable]: "colors.text.secondary",
                },
                addon: {
                    h: 8,
                    px: 2,
                    borderRadius: "md",
                },
            },
        },
        FormLabel: {
            baseStyle: {
                color: "text.primary",
                mb: 0,
                me: 0,
                fontSize: "md",
                lineHeight: "5",
            },
        },
        FormError: {
            baseStyle: {
                text: {
                    color: "text.danger",
                    fontSize: "sm",
                    lineHeight: "5",
                },
            },
        },
        Tabs: {
            baseStyle: {
                tabpanel: {
                    px: 0,
                },
            },
            variants: {
                line: {
                    tablist: {
                        borderBottom: "1px solid",
                    },
                    tab: {
                        position: "relative",
                        outline: "none",
                        transition: "none",
                        fontWeight: "medium",
                        h: 10,
                        mb: "-1px",
                        px: 4,
                        py: 0,
                        color: "text.secondary",
                        _selected: {
                            color: "primary.base",
                            borderColor: "primary.base",
                            _hover: {
                                borderColor: "primary.hover",
                            },
                        },
                        _hover: {
                            bg: "base.background",
                            color: "primary.hover",
                            borderColor: "base.border",
                        },
                        _focus: {
                            // Override app styles
                            outline: "none !important",
                        },
                        _focusVisible: {
                            boxShadow: "outline",
                        },
                    },
                },
            },
        },
        Menu: {
            baseStyle: {
                list: {
                    boxShadow: "lg",
                    borderRadius: "md",
                },
                item: {
                    fontSize: "md",
                    lineHeight: "5",
                    px: 4,
                    py: 2,
                    "> .chakra-menu__icon-wrapper": {
                        display: "flex",
                    },
                    [cssVar("menu-bg").variable]: "gray.background",
                    _focus: {
                        // Override app styles
                        outline: "none !important",
                    },
                },
                groupTitle: {
                    fontSize: "sm",
                    lineHeight: "4",
                    textTransform: "uppercase",
                    color: "text.tertiary",
                    letterSpacing: "0.03em",
                },
                divider: {
                    borderColor: "base.divider",
                },
            },
        },
        Modal: {
            baseStyle: {
                dialog: {
                    my: 40,
                    p: 6,
                },
                header: {
                    p: 0,
                    fontSize: "xl",
                    lineHeight: "6",
                    fontWeight: "medium",
                    color: "text.primary",
                },
                body: {
                    mt: 2,
                    p: 0,
                    color: "text.primary",
                    fontSize: "md",
                    lineHeight: "5",
                },
                footer: {
                    mt: 8,
                    p: 0,
                    gap: 3,
                },
            },
            defaultProps: {
                size: "lg",
            },
        },
        Progress: {
            baseStyle: {
                track: {
                    bg: "gray.200",
                    borderRadius: "lg",
                },
                filledTrack: {
                    transition: "all 100ms cubic-bezier(0.4, 0, 1, 1)",
                },
            },
            sizes: {
                sm: {
                    track: {
                        h: "8px",
                    },
                },
            },
            defaultProps: {
                size: "sm",
            },
        },
        Radio: {
            parts: ["container", "radio", "control", "label", "description"],
            baseStyle: {
                container: {
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                },
                radio: {
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    _disabled: {
                        cursor: "default",
                    },
                },
                control: {
                    // Required for rounded outline shadow
                    borderRadius: "full",
                    "rect:first-child": {
                        fill: cssVar("radio-background-color").reference,
                    },
                    "rect:last-child": {
                        stroke: cssVar("radio-border-color").reference,
                    },
                    circle: {
                        fill: cssVar("radio-dot-color").reference,
                    },
                    [cssVar("radio-background-color").variable]: "white",
                    [cssVar("radio-border-color").variable]: "colors.base.border",
                    [cssVar("radio-dot-color").variable]: "transparent",
                    _hover: {
                        [cssVar("radio-background-color").variable]: "colors.primary.background",
                        [cssVar("radio-border-color").variable]: "colors.primary.base",
                    },
                    _checked: {
                        [cssVar("radio-background-color").variable]: "colors.primary.background",
                        [cssVar("radio-border-color").variable]: "colors.primary.base",
                        [cssVar("radio-dot-color").variable]: "colors.primary.base",
                    },
                    _disabled: {
                        [cssVar("radio-background-color").variable]: "colors.base.border",
                        [cssVar("radio-border-color").variable]: "colors.base.border",
                        [cssVar("radio-dot-color").variable]: "colors.base.border",
                        _checked: {
                            [cssVar("radio-border-color").variable]: "colors.gray.500",
                            [cssVar("radio-dot-color").variable]: "colors.gray.500",
                        },
                    },
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                },
                label: {
                    fontWeight: "medium",
                    color: "text.primary",
                    ml: 2,
                },
                description: {
                    mt: 1,
                    color: "text.secondary",
                    [cssVar("hightouch-text-color").variable]: "colors.text.secondary",
                },
            },
            sizes: {
                md: {
                    control: {
                        boxSize: 4,
                    },
                    label: {
                        fontSize: "md",
                        lineHeight: "5",
                    },
                    description: {
                        fontSize: "md",
                        lineHeight: "5",
                        pl: 6,
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        Table: {
            sizes: {
                md: {
                    th: {
                        fontSize: "sm",
                        ":first-child": {
                            pl: 0,
                        },
                    },
                    td: {
                        fontSize: "md",
                        ":first-child": {
                            pl: 0,
                        },
                    },
                },
            },
            defaultProps: {
                size: "md",
            },
        },
        Slider: {
            parts: ["track", "thumb"],
            baseStyle: {
                container: {
                    "--slider-thumb-size": "var(--chakra-sizes-4)",
                },
                track: {
                    bg: "gray.200",
                    height: 0.5,
                },
                thumb: {
                    bg: "primary.base",
                    _disabled: {
                        bg: "forest.300",
                    },
                    _focusVisible: {
                        boxShadow: "outline",
                    },
                },
            },
        },
        Editable: {
            baseStyle: {
                preview: {
                    borderRadius: "md",
                },
                input: {
                    borderRadius: "md",
                },
                textarea: {
                    borderRadius: "md",
                },
            },
        },
        HightouchIcon: {
            baseStyle: {
                fill: "currentColor",
                display: "inline-flex",
                alignSelf: "center",
            },
        },
    }),
    fonts: {
        ...defaultTheme.fonts,
        body: "Inter, system-ui, sans-serif",
        heading: "Inter, system-ui, sans-serif",
        sharpSansDisplayNo1: "'Sharp Sans Display No 1', Inter, system-ui, sans-serif",
    },
    fontSizes: {
        sm: "12px",
        md: "13px",
        lg: "15px",
        xl: "18px",
        "2xl": "21px",
        "3xl": "24px",
        "4xl": "32px",
    },
    lineHeights: {
        normal: "normal",
        none: 1,
        "4": "16px",
        "5": "20px",
        "6": "24px",
        "8": "32px",
        "12": "48px",
    },
    breakpoints: {
        sm: "640px",
        md: "768px",
        lg: "1024px",
        xl: "1280px",
        "2xl": "1536px",
    },
    space: {
        ...defaultTheme.space,
        "0.5": "2px",
        "1": "4px",
        "1.5": "6px",
        "2": "8px",
        "2.5": "10px",
        "3": "12px",
        "3.5": "14px",
        "4": "16px",
        "5": "20px",
        "6": "24px",
        "7": "28px",
        "8": "32px",
        "9": "36px",
        "10": "40px",
        "12": "48px",
        "14": "56px",
        "16": "64px",
        "20": "80px",
        "24": "96px",
        "28": "112px",
        "32": "128px",
        "36": "144px",
        "40": "160px",
        "44": "176px",
        "48": "192px",
        "52": "208px",
        "56": "224px",
        "60": "240px",
        "64": "256px",
        "72": "288px",
        "80": "320px",
        "96": "384px",
    },
    sizes: {
        ...defaultTheme.sizes,
        "0.5": "2px",
        "1": "4px",
        "1.5": "6px",
        "2": "8px",
        "2.5": "10px",
        "3": "12px",
        "3.5": "14px",
        "4": "16px",
        "5": "20px",
        "6": "24px",
        "7": "28px",
        "8": "32px",
        "9": "36px",
        "10": "40px",
        "12": "48px",
        "14": "56px",
        "16": "64px",
        "20": "80px",
        "24": "96px",
        "28": "112px",
        "32": "128px",
        "36": "144px",
        "40": "160px",
        "44": "176px",
        "48": "192px",
        "52": "208px",
        "56": "224px",
        "60": "240px",
        "64": "256px",
        "72": "288px",
        "80": "320px",
        "96": "384px",
        "3xs": "224px",
        "2xs": "256px",
        xs: "320px",
        sm: "384px",
        md: "448px",
        lg: "512px",
        xl: "576px",
        "2xl": "672px",
        "3xl": "768px",
        "4xl": "896px",
        "5xl": "1024px",
        "6xl": "1152px",
        "7xl": "1280px",
        "8xl": "1440px",
    },
    radii: {
        xs: "2px",
        sm: "4px",
        md: "6px",
        lg: "8px",
        full: "9999px",
    },
    colors: {
        ...pick(defaultTheme.colors, [
            "white",
            "whiteAlpha",
            "black",
            "blackAlpha",
            "current",
            "transparent",
        ]),
        ...colors,
    },
    styles: {
        global: {
            ...defaultTheme.styles.global,
            body: {
                bg: "chakra-body-bg",
                color: "chakra-body-text",
                fontFamily: "body",
                transitionDuration: "normal",
                transitionProperty: "background-color",
            },
            ":root": {
                "--chakra-colors-chakra-body-text": "var(--chakra-colors-text-primary) !important",
                "--chakra-colors-chakra-border-color": "var(--chakra-colors-gray-300) !important",
            },
        },
    },
    shadows: {
        none: "none",
        xs: "0 1px 2px #1018280D",
        sm: "0 1px 3px #1018281A, 0 1px 2px #1018280F",
        md: "0 4px 8px -2px #1018281A, 0 2px 4px -2px #1018280F",
        lg: "0 12px 16px -4px #10182814, 0 4px 6px -2px #10182808",
        xl: "0 20px 24px -4px #10182814, 0 8px 8px -4px #10182808",
        "2xl": "0 24px 48px -12px #1018282E",
        "3xl": "0 32px 64px -12px #10182824",
        outline: "0px 0px 0px 4px #E0F3F5",
        outlineRed: "0 0 0 4px #fee4e2",
    },
};
export const extendTheme = (overrides) => {
    return mergeThemeOverride({}, theme, overrides);
};
